import { all, spread } from 'axios';
import { http } from '@/utils/http.js';
import { teamPath, teamRoot } from '@/utils/paths.js';

export const TeamsApi = {
  /**
   * Fetch all developer teams the user has access to
   * @returns {Promise.<Array>}
   */
  getDevelopers() {
    return http
      .get('/api/developers', { params: { paginate: false, sort: 'name:asc' } })
      .then((response) => filterDeletedTeams(response.data.data));
  },

  /**
   * Fetch all org teams the user has access to
   * @returns {Promise.<Array>}
   */
  getOrgs() {
    return http
      .get('/api/orgs', { params: { paginate: false, sort: 'name:asc' } })
      .then((response) => filterDeletedTeams(response.data.data));
  },

  /**
   * Consolidates the orgs and developers requests into one response
   * returning all teams the user has access to
   * @returns {Promise.<Object>}
   */
  all() {
    return all([this.getDevelopers(), this.getOrgs()]).then(
      spread((developers, orgs) => {
        return { developers, orgs };
      })
    );
  },

  /**
   * Fetch all teams the user *belongs* to
   * @returns {Promise.<Array>}
   */
  index() {
    return http.get('/api/users/current/teams', { params: { sort: 'name:asc' } }).then((response) => {
      let data = response.data.data;

      if ('developers' in data) {
        data.developers = filterDeletedTeams(data.developers);
      }

      if ('orgs' in data) {
        data.orgs = filterDeletedTeams(data.orgs);
      }

      return data;
    });
  },

  /**
   * Fetch the user's current team
   * @returns {Promise.<Object>}
   */
  show() {
    return http.get(teamPath()).then((response) => response.data.data);
  },

  /**
   * Creates a new team (currently dev team only)
   * @returns {Promise.<Object>}
   */
  store(data) {
    return http.post('/api/developers', data).then((response) => response.data.data);
  },

  /**
   * Update the user's current team
   * @param {Object} - request data
   * @returns {Promise.<Object>}
   */
  update(data) {
    return http.patch(teamPath(), data).then((response) => response.data.data);
  },

  /**
   * Deletes the user's current team
   * @returns {Promise.<Object>}
   */
  destroy(id, data) {
    return http.delete(`${teamRoot()}/${id}`, { data: data }).then((response) => response.data.data);
  },
};

// ////// //

/**
 * Remove 'Deleted' teams from the array
 * @param {Array} teams - list of org or dev teams
 * @returns {Array}
 */
function filterDeletedTeams(teams = []) {
  if (Array.isArray(teams) && teams.length) {
    teams = teams.filter((team) => team.status !== 'Deleted');
  }
  return teams;
}
